

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Card from '../../components/ui/Card';
import InputWithIcon from '../../components/ui/InputWithIcon';
import Button from '../../components/ui/Button';
import { getSessionUsername } from '../../utils/sessionUser';

const AddEditCustomerPage = ({ setView, customerId, setCustomerId }) => {
  const [formData, setFormData] = useState({
    Vname: '',
    Address1: '',
    City: '',
    Mobile: '',
    Mail: '',
    VType: 'customer',
    Sp_SqlAction: customerId ? 'update' : 'insert',
    Vid: customerId || 0,
    VCode: '',
    State: '',
    StateCode: '',
    Pincode: '',
    GST: '-',
    Phone: '',
    Fax: '',
    Active: 1,
    ComId: '0'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [stateList, setStateList] = useState([]);

  useEffect(() => {
    if (customerId && stateList.length > 0) {
      api.getCustomerForEdit(customerId)
        .then(data => {
          const customer =
            data.EditVendorOrimShortjs?.Table?.[0] || data.Table?.[0];

          if (customer) {
            let correctStateCode = "";
            let correctStateName = customer.State || "";

            // 1. Try by StateCode (must exist in master)
            const byCode = stateList.find(s => s.StateCode === customer.StateCode);

            // 2. Else try by StateName
            const byName = stateList.find(
              s => s.StateName.toLowerCase() === (customer.State || "").toLowerCase()
            );

            if (byCode) {
              correctStateCode = byCode.StateCode;
              correctStateName = byCode.StateName;
            } else if (byName) {
              correctStateCode = byName.StateCode;
              correctStateName = byName.StateName;
            } else if (customer.State) {
              // 3. If not found, add custom option dynamically
              correctStateCode = customer.StateCode || "custom";
              correctStateName = customer.State;

              // Inject into dropdown list so it shows up
              setStateList(prev => {
                if (!prev.some(s => s.StateCode === correctStateCode)) {
                  return [...prev, { StateCode: correctStateCode, StateName: correctStateName }];
                }
                return prev;
              });
            }

            setFormData(prev => ({
              ...prev,
              ...customer,
              Sp_SqlAction: "update",
              Vid: customer.vid || customer.Vid,
              Fax: customer.Fax ?? customer.fax ?? "",
              Active: customer.Active ?? customer.active ?? 1,
              State: correctStateName,
              StateCode: correctStateCode
            }));
          }
        })
        .catch(() => setError("Failed to fetch customer details."))
        .finally(() => setLoading(false));
    }
  }, [customerId, stateList]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const UsrName = getSessionUsername();
        const res = await api.getpartydetailsAPI(UsrName);

        if (res.Table) {
          setStateList(res.Table);
        } else if (res.GetpartydetailsAPIjs?.Table) {
          setStateList(res.GetpartydetailsAPIjs.Table);
        } else {
          setStateList([]);
        }
      } catch (err) {
        console.error("Failed to fetch states", err);
      }
    };

    fetchStates();
  }, []);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [id]: checked ? 1 : 0 }));
      return;
    }

    if (id === 'Mobile') {
      const regex = /^\d{0,10}$/;
      if (regex.test(value)) {
        setFormData(prev => ({
          ...prev,
          [id]: value,
          Phone: value
        }));
        // setMobileError(value.length > 0 && value.length < 10
        //   ? 'Mobile number must be 10 digits.'
        //   : '');
      }
      return;
    }

    if (id === "State") {
      const selected = stateList.find(st => st.StateCode === value);
      setFormData(prev => ({
        ...prev,
        State: selected?.StateName || "",
        StateCode: value
      }));
      return;
    }
    // default case
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Mobile validation
    // if (formData.Mobile.length !== 10) {
    //   setMobileError('Mobile number must be exactly 10 digits.');
    //   return;
    // }

    // setMobileError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        Fax: formData.Fax || "",
        State: formData.State || "",
        StateCode: formData.StateCode || ""
      };

      const result = await api.saveCustomer(payload);
      console.log("Save Customer API response:", result);

      // Correct response path
      const responseTable = result?.DMLVendorOrimRev?.Table || result?.DMLVendorOrimRevAPI?.Table || result?.DMLVendorOrim?.Table;
      const responseItem = responseTable?.[0];

      if (responseItem && responseItem.Status === "Success") {
        setSuccess(responseItem.Message || "Customer saved successfully!");
        setTimeout(() => {
          setView("customers");
          setCustomerId(null);
        }, 2000);
      } else {
        setError(responseItem?.Message || "Failed to save customer.");
      }

    } catch (err) {
      console.error(err);
      setError("An error occurred while saving.");
    } finally {
      setLoading(false);
    }
  };


  if (loading && customerId) return <Spinner />;

  return (
    <div className="w-full px-0 sm:max-w-4xl sm:pl-0 sm:pr-3 sm:mx-0">
      <Card className="w-full px-0 sm:px-6">
        {/* ✅ Title centered */}
        <h2 className="text-2xl font-bold text-center mb-6">
          {customerId ? "Edit Customer" : "Add New Customer"}
        </h2>

        <form onSubmit={handleSubmit} noValidate className="w-full">
          {error && <MessageBox message={error} onDismiss={() => setError('')} />}
          {success && <MessageBox message={success} type="success" />}


          <div className="mt-4 space-y-8">
            {/* Contact Info */}
            <div>
              <h3 className="pb-2 mb-4 text-lg font-semibold text-gray-700 border-b">
                Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-0 sm:gap-x-6">
                <InputWithIcon label="Customer Name*"
                  id="Vname"
                  value={formData.Vname}
                  onChange={handleChange} required />
                <InputWithIcon
                  label="Vendor Code*"
                  id="VCode"   // 👈 Corrected
                  value={formData.VCode}
                  onChange={handleChange}
                  required
                />
                <InputWithIcon
                  label="Mobile Number*"
                  id="Mobile"
                  value={formData.Mobile}
                  onChange={handleChange}
                  required
                  error={mobileError}
                />
                <InputWithIcon
                  label="Alt Mobile Number"
                  id="Fax"
                  type="tel"
                  value={formData.Fax}
                  onChange={handleChange}
                />
                <InputWithIcon
                  label="Email Address"
                  id="Mail"
                  type="email"
                  value={formData.Mail}
                  onChange={handleChange}
                  placeholder=""
                />
              </div>
            </div>

            {/* Address Info */}
            <div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-0 sm:gap-x-6">
                <div className="md:col-span-2">
                  <label htmlFor="Address1" className="block mb-1 font-semibold">
                    Address*
                  </label>
                  <textarea
                    id="Address1"
                    value={formData.Address1}
                    onChange={handleChange}
                    rows={4}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your address"
                  />
                </div>

                <InputWithIcon label="City" id="City" value={formData.City} onChange={handleChange} />
                <div>
                  <label htmlFor="State" className="block mb-1 font-semibold">
                    State
                  </label>
                  <select
                    id="State"
                    value={formData.StateCode || ""}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select State --</option>
                    {stateList.map((s) => (
                      <option key={s.StateCode} value={s.StateCode}>
                        {s.StateName}
                      </option>
                    ))}
                  </select>
                </div>

                <InputWithIcon label="Pincode" id="Pincode" value={formData.Pincode} onChange={handleChange} />
              </div>
            </div>

            {/* Active Checkbox */}
            <div>
              <label className="inline-flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="Active"
                  checked={formData.Active === 1}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded"
                />
                <span className="text-gray-700 font-semibold">Active</span>
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-6 mt-10 border-t">
            <Button
              type="submit"
              disabled={loading}
              className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
            >
              {loading ? 'Saving...' : 'Save Customer'}
            </Button>

            <Button
              type="button"
              onClick={() => { setView('customers'); setCustomerId(null); }}
              className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600"
            >
              Cancel
            </Button>

          </div>

        </form>
      </Card>
    </div>
  );
};

export default AddEditCustomerPage;

